/**
 * Guards for keyboard shortcuts: no shortcut acts while an overlay is open or
 * while focus is in a text field.
 */
import { useEffect } from "react";

/** Is a dialog (a confirmation included), drawer, popover (a date picker included) or select list open? */
export const overlayOpen = (): boolean =>
  document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="listbox"][data-state="open"]',
  ) !== null;

/**
 * Is focus in a text field, where keys type rather than act, or on a select
 * trigger, whose letters jump to an option? Read-only fields are not.
 */
export const typing = (target: EventTarget | null): boolean =>
  target instanceof HTMLElement &&
  (target.isContentEditable ||
    target.closest('[role="combobox"], [aria-haspopup="listbox"]') !== null ||
    (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) &&
      !(target as HTMLInputElement).readOnly));

/** Alt+← goes back along a drill path: trial balance → ledger → entry → source. */
export function useBackKey(): void {
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (!event.altKey || event.key !== "ArrowLeft" || overlayOpen() || typing(event.target))
        return;
      event.preventDefault();
      window.history.back();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
}
